import { useState } from "react";

import { NvButton, NvExternalIcon } from "@/components/navigator";
import { safeHttpUrl } from "@/lib/safe-url";
import { cn } from "@/lib/utils";
import type { DetailAdoption } from "@/services/navigator/agent-detail-service";
import { recordEvent } from "@/services/navigator/finder-service";

import { NvCoinIcon, NvFlaskIcon, NvStackIcon, NvTagIcon } from "./detail-icons";
import { DetailSection } from "./detail-parts";

// 도입 조건 (설계서 slide 48 · Figma 03-01/05): 상단 4타일(배포 · 가격 · PoC · 도입 비용대, 값 없는 타일은 빼고 균등 배치) +
// 구동 요건 아코디언 4(배포 및 망 환경 → AI-Ready Data → 시스템 연동 → 권한 및 운영, 첫 항목만 펼침 · 복수 펼침 허용, 1차+WF 이상).
// PoC 비용 정책이 URL이면 '공급사 가격 안내' 링크로, 체험 URL이 있으면 '직접 체험해 보기' 버튼(새 창).

const TILE_ICONS = { deployment: NvStackIcon, pricing: NvTagIcon, poc: NvFlaskIcon, price_range: NvCoinIcon } as const;

function Accordion({ items }: { items: DetailAdoption["requirements"] }) {
  const [open, setOpen] = useState<Set<number>>(() => new Set([0]));
  const toggle = (i: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  return (
    <div className="border-t border-nv-border">
      {items.map((item, i) => {
        const isOpen = open.has(i);
        return (
          <div key={item.title} className="border-b border-nv-border">
            <h3>
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => toggle(i)}
                className="flex w-full items-center justify-between gap-4 py-[17px] text-left text-[17px] leading-[27px] text-nv-text-primary"
              >
                {item.title}
                <span aria-hidden className="relative h-[14px] w-[14px] shrink-0 text-nv-text-secondary">
                  <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current" />
                  {!isOpen && <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current" />}
                </span>
              </button>
            </h3>
            {isOpen && <p className="max-w-[908px] whitespace-pre-line pb-5 text-[15px] leading-6 text-nv-text-secondary">{item.body}</p>}
          </div>
        );
      })}
    </div>
  );
}

export function AdoptionSection({ data, agentId }: { data: DetailAdoption; agentId: string }) {
  const playground = safeHttpUrl(data.playground_url);
  return (
    <DetailSection id="adoption" title="도입 조건">
      <div className="flex flex-col gap-10">
        {data.tiles.length > 0 && (
          // 값 없는 타일은 빼고 남은 타일을 균등 배치, 타일 높이는 통일
          <div className="grid auto-rows-fr gap-6 sm:grid-cols-2 xl:[grid-template-columns:repeat(var(--tiles),minmax(0,1fr))]" style={{ ["--tiles" as string]: data.tiles.length }}>
            {data.tiles.map((tile) => {
              const Icon = TILE_ICONS[tile.key as keyof typeof TILE_ICONS] ?? NvTagIcon;
              const link = safeHttpUrl(tile.link);
              return (
                <div key={tile.key} className="flex min-h-[147px] flex-col gap-[6px] rounded-nv-card border border-nv-border bg-nv-surface p-6 pt-[22px]">
                  <span className="flex items-center gap-2 text-[13px] leading-[21px] text-nv-text-tertiary">
                    <Icon size={20} />
                    {tile.label}
                  </span>
                  {tile.text && <p className="break-keep text-[15px] leading-6 text-nv-text-primary">{tile.text}</p>}
                  {link && (
                    <a
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[14px] leading-[22px] text-nv-brand-text underline-offset-2 hover:underline"
                    >
                      {tile.link_label}
                      <NvExternalIcon size={13} />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {playground && (
          <div>
            <NvButton variant="secondary" asChild>
              <a href={playground} target="_blank" rel="noreferrer" onClick={() => recordEvent("playground_click", agentId)}>
                직접 체험해 보기
                <NvExternalIcon size={14} />
              </a>
            </NvButton>
          </div>
        )}
        {data.requirements.length > 0 && <Accordion items={data.requirements} />}
      </div>
    </DetailSection>
  );
}
